import { ReportData, ReportSection, CombinedReportFilterOption, EncryptedData, Ngrams, EncryptedPayload } from "~/types/global";
import { CryptoResult } from "~/workers/crypto/interfaces";
import { REPORT_CONSTANTS } from "./constants";

export interface CombinedSearchArgs {
  [key: string]: string | number | object;
}

type KeyCounters = Record<string, number>;

type DecryptFunction = (encryptedData: EncryptedPayload, password?: string) => Promise<CryptoResult<any>>;

const searchArgs = REPORT_CONSTANTS.new_report.report_search_args

/**
 * Handles the case where a key already exists in the combinedArgs object.
 * It renames the existing key and adds the new key with a suffix.
 *
 * @param combinedArgs - The object containing all combined search arguments
 * @param keyCounters - An object tracking the count of each key
 * @param key - The key to handle
 * @param value - The value associated with the key
 */
export const handleExistingKey = (
  combinedArgs: CombinedSearchArgs,
  keyCounters: KeyCounters,
  key: string,
  value: string | number | object
): void => {
  if (keyCounters[key] === undefined) {
    // Pega a primeira chave duplicada a adiciona o sufixo
    const existingValue = combinedArgs[key];
    delete combinedArgs[key];
    combinedArgs[`${key}_1`] = existingValue;
    keyCounters[key] = 1;
  }

  keyCounters[key]++;
  combinedArgs[`${key}_${keyCounters[key]}`] = value;
};

const processReportSearchArgs = (
  report: ReportData,
  combinedArgs: CombinedSearchArgs,
  keyCounters: KeyCounters
): void => {

  const reportSearchArgs = report?.[searchArgs];
  const isNotObject = typeof reportSearchArgs !== "object";
  const isNull = reportSearchArgs === null;

  if (!reportSearchArgs || isNotObject || isNull) return;

  if ('encrypted' in reportSearchArgs && 'iv' in reportSearchArgs) {
    return;
  }

  const searchArgsObj = reportSearchArgs as Record<string, string | number | object>;

  Object.entries(searchArgsObj).forEach(([key, value]) => {
    if (combinedArgs[key] !== undefined) {
      handleExistingKey(combinedArgs, keyCounters, key, value);
    } else {
      combinedArgs[key] = value;
    }
  });
};

export const combineReportSearchArgs = (reportsDetails: ReportData[]): CombinedSearchArgs => {
  if (!reportsDetails || reportsDetails.length === 0) {
    return {};
  }

  const combinedReportSearchArgs: CombinedSearchArgs = {};
  const keyCounters: Record<string, number> = {};

  reportsDetails.forEach((report) => {
    processReportSearchArgs(report, combinedReportSearchArgs, keyCounters);
  });

  // Normaliza as chaves com sufixos (_1, _2) para arrays sem duplicatas
  const normalizedArgs: CombinedSearchArgs = {};
  const groupedKeys: Record<string, Set<string>> = {};

  Object.entries(combinedReportSearchArgs).forEach(([key, value]) => {
    const match = key.match(/^(.+)_(\d+)$/);

    if (match) {
      const baseKey = match[1];
      if (!groupedKeys[baseKey]) {
        groupedKeys[baseKey] = new Set();
      }

      if (Array.isArray(value)) {
        value.forEach(v => groupedKeys[baseKey].add(v));
      } else {
        groupedKeys[baseKey].add(value as string);
      }
    } else {
      normalizedArgs[key] = value;
    }
  });

  // Converte Sets para arrays
  Object.entries(groupedKeys).forEach(([baseKey, valueSet]) => {
    const uniqueValues = Array.from(valueSet);
    normalizedArgs[baseKey] = uniqueValues.length === 1 ? uniqueValues[0] : uniqueValues;
  });

  return normalizedArgs;
};

const reportTypeKey = REPORT_CONSTANTS.new_report.report_type;
const dataKey = REPORT_CONSTANTS.new_report.data;
const reportNameKey = REPORT_CONSTANTS.new_report.report_name;
const reportIdKey = REPORT_CONSTANTS.new_report.report_id;

/**
 * A report's own `ReportSection[]`, read out of its decrypted `data`
 * envelope (`{[report_type]: ReportSection[]}` — the same shape every
 * non-combined report uses). `[]` for any malformed/missing shape; never
 * throws — a source report with an odd shape just contributes nothing.
 */
function extractOwnSections(report: ReportData): ReportSection[] {
  const type = report?.[reportTypeKey];
  const data = report?.[dataKey] as unknown;
  if (typeof type !== "string" || !data || typeof data !== "object") return [];
  const sections = (data as Record<string, unknown>)[type];
  if (!Array.isArray(sections)) return [];
  // A combinado source holds `[{ combined_data, report_sources_data }]`, not sections.
  const own =
    type === REPORT_CONSTANTS.types.combinado
      ? (sections[0] as { report_sources_data?: unknown } | undefined)?.report_sources_data
      : sections;
  // Drops untitled entries left by older merges that stored the wrapper itself as a section.
  return Array.isArray(own) ? (own as ReportSection[]).filter(isTitledSection) : [];
}

export function isTitledSection(section: unknown): section is ReportSection {
  return typeof (section as ReportSection | null)?.title === "string";
}

/**
 * Builds the `{ combined_data, report_sources_data }` pair every consumer of
 * a "combinado" report expects inside `data.combinado[0]` — the report's own
 * detail page (`ReportContext.tsx`'s `filterCombinadoSections`), PDF/DOCX
 * export, and the AI summary all read it, but nothing on the backend or
 * frontend ever BUILT it: `merge_reports_handler` just nested the raw source
 * reports under `data.combinado`, so every merged report rendered as
 * completely empty once it actually finished (see the "etapas" bug this
 * fixes, 2026-09-08) — `filterCombinadoSections` throws "Sem dados
 * combinados disponíveis" the moment it can't find this wrapper shape.
 *
 * - `report_sources_data`: every source's own sections, concatenated,
 *   each tagged with its `user_reports_id` — the "por relatório" filter view
 *   (`CombinedReportFilter.tsx`).
 * - `combined_data`: the same sections grouped by `(title, subsection)`
 *   across every source, with matching sections' `data` arrays
 *   concatenated — the "todos os relatórios juntos" view.
 */
/**
 * Deterministic canonical string for a value, used only to decide whether
 * two merged data items are *exactly* the same record. Object keys are
 * sorted so key order never produces a false "different" verdict, and any
 * per-item `source` key is ignored — two records that differ only by which
 * source reported them ARE the same record and must collapse to one (their
 * sources get merged separately, see `mergeDataItems`).
 */
function canonicalItemKey(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalItemKey).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const parts = Object.keys(obj)
    .filter((k) => k !== "source")
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonicalItemKey(obj[k])}`);
  return `{${parts.join(",")}}`;
}

/** Union of two `source` values (array | string | undefined) into a deduped array. */
function unionSources(a: unknown, b: unknown): string[] {
  const toArr = (s: unknown): string[] =>
    Array.isArray(s) ? (s as string[]) : s ? [s as string] : [];
  return Array.from(new Set([...toArr(a), ...toArr(b)]));
}

/**
 * Concatenate the `data` arrays of sections sharing a (title, subsection)
 * key while deduplicating records that are *exactly* equal. Distinct records
 * (e.g. two different "Processos" numbers) are preserved; identical records
 * coming from more than one source collapse to a single entry whose
 * per-item `source` (when present) is the union of both.
 */
function mergeDataItems(
  existing: Array<Record<string, any>>,
  incoming: Array<Record<string, any>>
): Array<Record<string, any>> {
  const byKey = new Map<string, Record<string, any>>();
  const result: Array<Record<string, any>> = [];

  const add = (item: Record<string, any>) => {
    const key = canonicalItemKey(item);
    const seen = byKey.get(key);
    if (!seen) {
      byKey.set(key, item);
      result.push(item);
      return;
    }
    // Exact duplicate: keep one, merge per-item sources if either has them.
    if ("source" in seen || (item && "source" in item)) {
      seen.source = unionSources(seen.source, item?.source);
    }
  };

  (existing || []).forEach((item) => item && add(item));
  (incoming || []).forEach((item) => item && add(item));
  return result;
}

export function combineReportSections(reportsDetails: ReportData[]): {
  combinedData: ReportSection[];
  reportSourcesData: ReportSection[];
} {
  const reportSourcesData: ReportSection[] = [];

  reportsDetails.forEach((report) => {
    const userReportsId = report?.[reportIdKey] as string | undefined;
    extractOwnSections(report).forEach((section) => {
      reportSourcesData.push({ ...section, user_reports_id: userReportsId });
    });
  });

  const groups = new Map<string, ReportSection>();
  reportSourcesData.forEach((section) => {
    const key = `${section.title}::${section.subsection ?? ""}`;
    const existing = groups.get(key);
    if (!existing) {
      // First section for this key: dedup its own data too, in case a single
      // source already carried exact-duplicate records.
      const data = mergeDataItems([], section.data || []);
      groups.set(key, { ...section, data, data_count: data.length });
      return;
    }
    existing.data = mergeDataItems(existing.data || [], section.data || []);
    existing.data_count = existing.data.length;
    existing.source = Array.from(new Set([...(existing.source || []), ...(section.source || [])]));
  });

  return { combinedData: Array.from(groups.values()), reportSourcesData };
}

/**
 * Builds `report_sources_used` — the per-source metadata list the
 * "por relatório" dropdown filter (`CombinedReportFilter.tsx`) reads to
 * label each option. Same gap as `combineReportSections`: nothing ever
 * built this either, so the filter had nothing to list.
 */
export function buildReportSourcesUsed(reportsDetails: ReportData[]): CombinedReportFilterOption[] {
  return reportsDetails.map((report) => ({
    report_type: (report?.[reportTypeKey] ?? "") as string,
    report_input_value: (report?.[searchArgs] ?? "") as string | Record<string, any>,
    user_reports_id: (report?.[reportIdKey] ?? "") as string,
    report_name: (report?.[reportNameKey] ?? "") as string,
  }));
}

/**
 * Fetches and decrypts reports based on their IDs.
 */
export const fetchAndDecryptReports = async (
  reportIds: string[],
  fetchReportById: (id: string) => Promise<unknown>,
  decryptReportPayload: (report: ReportData, decryptFn: DecryptFunction) => Promise<any>,
  decryptData: DecryptFunction
): Promise<ReportData[]> => {

  const reportsDetailsPromises = reportIds.map(async (reportId) => {
    const report = await fetchReportById(reportId);

    if (!report || typeof report !== 'object') {
      throw new Error(`Failed to fetch report with ID: ${reportId}. Report data is null or invalid.`);
    }

    return decryptReportPayload(report as ReportData, decryptData);
  });

  const results = await Promise.all(reportsDetailsPromises);
  return results as ReportData[];
};